import { CSSProperties } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { Avatar, Text } from '@hhgtech/hhg-components/mantine'
import { Container, Box, BoxProps, Flex, Stack, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import { HealthtoolReviewer } from 'interfaces/types'
import { renderDate } from 'utils/renderDate'

import { EllipseImage } from './ellipseImage'
import useStyles from './index.styles'
import {
  HealthToolBreadcrumb,
  HealthToolBreadcrumbProps,
} from '../healthToolBreadcrumb'

export type Props = {
  heading: string
  description: string
  medicalReview?: JSX.Element
  bannerUrl?: string
  bannerUrlLarge?: string
  bannerColor?: string
  style?: CSSProperties
  className?: string
  isMobile?: boolean
  icon?: JSX.Element
  isHomePage?: boolean
  isEmbed?: boolean
  reviewBy?: HealthtoolReviewer
  isPink?: boolean
  breadcrumbProps?: HealthToolBreadcrumbProps
} & BoxProps

type PropsMedicalReview = {
  isMobile?: Props['isMobile']
  reviewBy?: Props['reviewBy']
  size?: 'default' | 'small'
}

const ToolsBannerCardV2 = ({
  heading,
  isMobile,
  description,
  icon,
  reviewBy,
  isPink,
  breadcrumbProps,
  styles,
  classNames,
  className,
  ...boxProps
}: Props) => {
  const { classes, cx } = useStyles(
    { isPink },
    {
      name: 'ToolsBannerCardV2',
      styles,
      classNames,
    },
  )

  return (
    <Box className={cx(className, classes.root)} {...boxProps}>
      <Container
        size="lg"
        className="container"
        style={{ position: 'relative' }}
      >
        <Box className={classes.inner}>
          <Stack spacing={rem(24)}>
            {!isMobile && breadcrumbProps && (
              <HealthToolBreadcrumb
                {...breadcrumbProps}
                style={{
                  margin: 0,
                  padding: 0,
                }}
              />
            )}
            <Stack
              spacing={rem(12)}
              sx={() => ({
                width: '100%',
                maxWidth: rem(744),
              })}
            >
              <Stack spacing={rem(8)}>
                <Heading tag="h2" as="h1">
                  {heading}
                </Heading>
                <Text
                  size="p3"
                  as="div"
                  sx={(theme) => ({
                    [theme.fn.smallerThan('sm')]: {
                      ...(!reviewBy?.reviewer && {
                        width: `calc(100% - 80px)`,
                      }),
                    },
                  })}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: description,
                    }}
                  ></div>
                </Text>
              </Stack>
              {reviewBy?.avatar && reviewBy?.reviewer && (
                <Box
                  className={classes.medicalReview}
                  data-icon={String(!!icon)}
                >
                  <MedicalReview isMobile={isMobile} reviewBy={reviewBy} />
                </Box>
              )}
            </Stack>
            {icon && (
              <Box className={classes.thumbnailCol}>
                <EllipseImage
                  classNames={{
                    root: classes.thumbnail,
                    wrapper: classes.thumbnailWrapper,
                    inner: classes.thumbnailInner,
                  }}
                  icon={icon}
                  isPink={isPink}
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

const MedicalReview = ({
  reviewBy,
  isMobile,
  size = 'default',
}: PropsMedicalReview) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()

  if (!reviewBy?.avatar || !reviewBy?.reviewer) return null

  return (
    <Flex
      gap={12}
      sx={(theme) => ({
        alignItems: 'flex-start',
        [theme.fn.smallerThan('sm')]: {
          position: 'relative',
          zIndex: 1,
        },
      })}
    >
      <Box pos="relative" top={3}>
        <Avatar
          size={size === 'default' ? 32 : 20}
          src={reviewBy.avatar}
          withIndicator
          indicatorConfig={{
            size: 18,
            label: (
              <img
                loading="lazy"
                width="16"
                src={`${assetImagePath}/reviewer-medical.svg`}
                alt=""
              />
            ),
          }}
          styles={{
            root: {
              '.mantine-Indicator-indicator': {
                bottom: size === 'default' ? '7px' : '4px',
                right: size === 'default' ? '5px' : '3px',
              },
              '.mantine-Avatar-placeholder': {
                border: 0,
              },
            },
          }}
        />
      </Box>
      <Text
        size={isMobile ? 'p3' : 'p4'}
        color="gray.6"
        as="div"
        sx={(theme) => ({
          lineHeight: `${rem(24)}!important`,
          [theme.fn.largerThan('sm')]: {
            div: {
              display: 'inline',
            },
          },
        })}
      >
        {f({ id: 'articleAuthor.title.medical' })}{' '}
        <span>
          <a href={reviewBy.link || '#'} style={{ color: '#262626' }}>
            {reviewBy.reviewer}
          </a>
          {reviewBy.date && ' • '}
        </span>
        {reviewBy.date && <span>{renderDate(reviewBy.date, locale)}</span>}
      </Text>
    </Flex>
  )
}

ToolsBannerCardV2.MedicalReview = MedicalReview
export { ToolsBannerCardV2 }
